/**
 * The public marketing pages that are prerendered to real HTML at build time.
 *
 * One list, three readers:
 *   - `scripts/prerender.mjs` renders each to `dist/prerendered/<slug>.html` and writes the sitemap,
 *   - `worker/index.ts` serves that file for the path instead of the empty SPA shell,
 *   - `marketingLayout.container.tsx` sets the same `<title>` on client-side navigation.
 *
 * The prerendered files deliberately do *not* live at `dist/index.html`: that file is also the SPA
 * fallback for every app route, and a `/workspace` deep link would flash the landing page before
 * React replaced it. Add a page here and to the marketing routes in `App.tsx` together.
 */

export interface PublicPage {
  path: string;
  /** File name under `dist/prerendered/`. Never `index` — the asset server redirects that to `/`. */
  slug: string;
  title: string;
  description: string;
}

const SITE_NAME = 'CensorMe';

export const PUBLIC_PAGES: PublicPage[] = [
  {
    path: '/',
    slug: 'home',
    title: `${SITE_NAME} — Cut or mute every flagged word, straight into your editor`,
    description:
      'CensorMe transcribes spoken-word audio and builds a ready-to-edit XML timeline that cuts or ' +
      'silences every word on your list. Works with Premiere Pro, DaVinci Resolve and Final Cut Pro.',
  },
  {
    path: '/docs',
    slug: 'docs',
    title: `Documentation — ${SITE_NAME}`,
    description:
      'How to upload audio, transcribe it, build word lists and import the censored XML timeline ' +
      'into Premiere Pro, DaVinci Resolve or Final Cut Pro.',
  },
  {
    path: '/downloads',
    slug: 'downloads',
    title: `Download CensorMe Mapper — ${SITE_NAME}`,
    description:
      'CensorMe Mapper is the free desktop companion that relinks the media paths in an exported XML ' +
      'timeline. Builds for Windows and macOS.',
  },
  {
    path: '/terms',
    slug: 'terms',
    title: `Terms of Service — ${SITE_NAME}`,
    description: 'The terms that govern your use of CensorMe, including billing, refunds and consumer rights.',
  },
  {
    path: '/contact',
    slug: 'contact',
    title: `Contact — ${SITE_NAME}`,
    description: 'Questions about CensorMe, your account or your subscription? Get in touch with support.',
  },
];

/** The page for a pathname, tolerating a trailing slash. */
export function findPublicPage(pathname: string): PublicPage | undefined {
  const normalised = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return PUBLIC_PAGES.find((page) => page.path === normalised);
}
